export type AccessLevelName =
  | "Observer"
  | "Finance Executive"
  | "Finance Manager"
  | "Administrator";

type PermissionItem = {
  label: string;
  enabled: boolean;
  inheritedFrom?: AccessLevelName;
};

export const accessLevelClassName: Record<AccessLevelName, string> = {
  Observer: "bg-[#E5E7EB] text-[#505E8E]",
  "Finance Executive": "bg-[#DBEAFE] text-[#0284C7]",
  "Finance Manager": "bg-[#6687FF1A] text-[#6687FF]",
  Administrator: "bg-[#E0E7FF] text-[#3730A3]",
};

export const accessLevelDisplayName: Record<AccessLevelName, string> = {
  Observer: "Level 1",
  "Finance Executive": "Level 2",
  "Finance Manager": "Level 3",
  Administrator: "Level 4",
};

export const accessLevels = [
  {
    level: "Level 1",
    name: "Observer" as AccessLevelName,
    description: "Read-only access across dashboard, statements, history, reports and view-only approvals.",
    permissions: [
      "Dashboard",
      "Statements",
      "Transfers History",
      "Beneficiaries - view only",
      "Pending Approvals - view only",
      "Reports",
    ],
    restrictions: [
      "Cannot create transfers",
      "Cannot edit beneficiaries",
      "Cannot approve transfers",
      "Cannot manage users",
      "Cannot configure approval workflows",
    ],
  },
  {
    level: "Level 2",
    name: "Finance Executive" as AccessLevelName,
    description: "Can create transfer work and manage beneficiary operations, without approval or admin powers.",
    permissions: [
      "Everything in Observer",
      "Initiate Transfer",
      "Save Draft",
      "Add Beneficiary",
      "Edit Beneficiary",
      "Upload Supporting Documents",
      "Export Reports",
    ],
    restrictions: [
      "Cannot approve transfers",
      "Cannot manage users",
      "Cannot change roles",
      "Cannot edit approval workflows",
    ],
  },
  {
    level: "Level 3",
    name: "Finance Manager" as AccessLevelName,
    description: "Can manage users and role assignment for day-to-day finance operations.",
    permissions: [
      "Everything in Finance Executive",
      "Invite Users",
      "Edit Users",
      "Activate / Suspend Users",
      "Assign predefined roles",
      "View Audit Logs",
    ],
    restrictions: [
      "Cannot edit Approval Workflows",
      "Cannot edit Approval Groups",
      "Cannot configure Permissions",
    ],
  },
  {
    level: "Level 4",
    name: "Administrator" as AccessLevelName,
    description: "Full system configuration access, including workflow, role and permission management.",
    permissions: [
      "Everything in Finance Manager",
      "Approval Groups",
      "Approval Workflows",
      "Permissions Management",
      "Settings",
      "Bank Accounts",
      "Configure Workflow",
      "Create Custom Roles",
      "Edit Permissions",
      "Delete Roles",
      "System Configuration",
    ],
    restrictions: ["No restrictions"],
  },
];

export const roleRows = [
  {
    slug: "viewer",
    name: "Viewer",
    accessLevel: "Observer" as AccessLevelName,
    description: "Level 1 read-only access for users who need visibility into dashboards, statements, transfers and reports without operational permissions.",
    users: 15,
    status: "Active",
    type: "System",
    deletable: false,
  },
  {
    slug: "initiator",
    name: "Initiator",
    accessLevel: "Finance Executive" as AccessLevelName,
    description: "Level 2 operational access to initiate transfers, save drafts, manage beneficiaries and download payout records.",
    users: 24,
    status: "Active",
    type: "System",
    deletable: false,
  },
  {
    slug: "manager",
    name: "Manager",
    accessLevel: "Finance Manager" as AccessLevelName,
    description: "Level 3 supervisory access to manage payout users, assign predefined roles and review audit activity.",
    users: 8,
    status: "Active",
    type: "System",
    deletable: false,
  },
  {
    slug: "administrator",
    name: "Administrator",
    accessLevel: "Administrator" as AccessLevelName,
    description: "Level 4 full administrative access to approval workflows, permission controls, settings and payout configuration.",
    users: 2,
    status: "Active",
    type: "System",
    deletable: false,
  },
];

export const permissionRows = [
  ["view_dashboard", "Can view dashboard and summary information.", "Dashboard", "System", "Levels 1, 2, 3, 4", "Active"],
  ["view_statements", "Can view and download payout statements.", "Statements", "System", "Levels 1, 2, 3, 4", "Active"],
  ["view_transfer_history", "Can view transfer history and transfer details.", "Transfers", "System", "Levels 1, 2, 3, 4", "Active"],
  ["initiate_transfer", "Can create and submit new transfers.", "Transfers", "System", "Levels 2, 3, 4", "Active"],
  ["save_transfer_draft", "Can save transfer requests as drafts.", "Transfers", "System", "Levels 2, 3, 4", "Active"],
  ["manage_beneficiaries", "Can add and edit beneficiary records.", "Beneficiaries", "System", "Levels 2, 3, 4", "Active"],
  ["approve_transfer", "Can approve or reject transfer requests.", "Approvals", "System", "Level 4", "Active"],
  ["manage_users", "Can invite, edit, activate and suspend users.", "Administration", "System", "Levels 3, 4", "Active"],
  ["manage_roles", "Can create, clone and update custom roles.", "Administration", "Custom", "Level 4", "Active"],
  ["manage_permissions", "Can update role permission mappings.", "Administration", "Custom", "Level 4", "Active"],
  ["configure_workflow", "Can configure approval workflows.", "Approvals", "Custom", "Level 4", "Active"],
];

export const permissionCategories: Array<{
  name: string;
  permissions: PermissionItem[];
}> = [
  {
    name: "Dashboard",
    permissions: [
      { label: "View Dashboard", enabled: true, inheritedFrom: "Observer" },
    ],
  },
  {
    name: "Transfers",
    permissions: [
      { label: "View Transfers", enabled: true, inheritedFrom: "Observer" },
      { label: "Initiate Transfer", enabled: true, inheritedFrom: "Finance Executive" },
      { label: "Edit Transfer", enabled: true, inheritedFrom: "Finance Executive" },
      { label: "Cancel Transfer", enabled: false },
    ],
  },
  {
    name: "Beneficiaries",
    permissions: [
      { label: "View Beneficiaries", enabled: true, inheritedFrom: "Observer" },
      { label: "Add Beneficiary", enabled: true, inheritedFrom: "Finance Executive" },
      { label: "Edit Beneficiary", enabled: true, inheritedFrom: "Finance Executive" },
      { label: "Delete Beneficiary", enabled: false },
    ],
  },
  {
    name: "Approvals",
    permissions: [
      { label: "View Pending Approvals", enabled: true, inheritedFrom: "Observer" },
      { label: "Approve Transfers", enabled: true, inheritedFrom: "Administrator" },
      { label: "Approval Workflows", enabled: true, inheritedFrom: "Administrator" },
      { label: "Approval Groups", enabled: true, inheritedFrom: "Administrator" },
    ],
  },
  {
    name: "Users",
    permissions: [
      { label: "View Users", enabled: true, inheritedFrom: "Finance Manager" },
      { label: "Invite Users", enabled: true, inheritedFrom: "Finance Manager" },
      { label: "Edit Users", enabled: true, inheritedFrom: "Finance Manager" },
      { label: "Manage Settings", enabled: true, inheritedFrom: "Administrator" },
    ],
  },
];

export const accessMatrix = [
  ["Dashboard", "Yes", "Yes", "Yes", "Yes"],
  ["Statements", "Yes", "Yes", "Yes", "Yes"],
  ["Transfer History", "Yes", "Yes", "Yes", "Yes"],
  ["Initiate Transfer", "No", "Yes", "Yes", "Yes"],
  ["Beneficiaries", "View", "Edit", "Edit", "Full"],
  ["Users", "No", "No", "Yes", "Yes"],
  ["Roles", "No", "View", "View", "Edit"],
  ["Permissions", "No", "No", "View", "Edit"],
  ["Approval Groups", "No", "No", "No", "Yes"],
  ["Approval Workflows", "No", "No", "No", "Yes"],
  ["Settings", "No", "No", "No", "Yes"],
];

export const auditTrail: string[] = [];
